/**
 * ModelFinderCore — the tree-consumption orchestration shared by BOTH finder
 * controllers (PageFinderController, SsrFinderController).
 *
 * **Plan-driven (ADR-0009/0010, spec §3.3).** The backend ships a column plan
 * (`columns: Level[]`) per subtree response; this controller renders the plan,
 * positionally, and owns NO level policy. It reads
 * `treeService.columnPlan(selectedId)` for the current deepest selection (D6),
 * builds an ordered `itemsByPosition` (position 0 = children of root; position i
 * = children of the selected node at position i-1), and drives
 * `view.renderPlan(plan, items)` → `view.select(position, id)` per path node
 * (ordering D11) → on a LIVE select only, `view.scrollActiveIntoView(...)` (D12).
 *
 * Lifecycle (unchanged from the `/datte` model):
 *  - **Forward click** (`onUserSelect`): `selectedId = clicked.id`; branch on
 *    `clicked.hasChildren` BEFORE any fetch (§3.5/AC-S10). `true` →
 *    `ensureChildren(id)` → re-render from the plan; `false` → terminal model:
 *    NO fetch, `view.showCta(...)`.
 *  - **Restore / deep-link** (`restore(selectedId)`): `ensureChildren(selectedId)`
 *    fills the `ancestors` spine, a `pathTo` fan-out fetches every column's
 *    children, then one plan-driven render fills + selects the whole path. Never
 *    scrolls (D12).
 *
 * The plan is level-AGNOSTIC on the client: `plan[i]` is a level number used
 * ONLY to look up that column's title (`applyLabels`, keyed by level). The one
 * remaining legitimate `node.level` use is `resetLabelsAfter` (D16).
 *
 * @see docs/speed-optimization/research/2026-06-07 - model-finder-column-plan-and-modal/spec.md §3.3
 */

import { ModelFinderStateManager } from '../model-finder-state';
import { ModelFinderSelection } from '../domain/selection';
import type { Level } from '../domain/levels';
import { type NodeId, type Position, position as positionOf } from '../domain/brand';
import type { Node } from '../data/wire';
import { ROOT_ID, type TreeService } from '../data/tree-service';
import { EndpointError } from '../data/endpoint';
import type { LevelLabel, ModelFinderView } from '../view/view';
import type { LevelsResponse } from '../data/wire';

export class ModelFinderCore {
  private readonly selection = new ModelFinderSelection();

  /**
   * @param treeService   the RAM tree (one per controller, seeded from the hash)
   * @param view          the DOM render surface
   * @param baseLabels    the `/levels` rows (backend-owned label text)
   * @param onAfterSelect optional hook fired after each LIVE selection is
   *   persisted — each finder mirrors the single id into its own deep-link URL
   *   param (`datte_mf` / `datte_mfm`). Not called during restore (SEC-4-5).
   */
  constructor(
    private readonly treeService: TreeService,
    private readonly view: ModelFinderView,
    private readonly baseLabels: LevelsResponse,
    private readonly onAfterSelect?: (selectedId: NodeId | null) => void
  ) {
    this.view.onUserSelect((position, node) => {
      // A rejected onSelect used to vanish as an unhandled rejection: the column
      // simply never populated, with nothing in the console and nothing on screen.
      // The empty UI is the RIGHT customer-facing outcome (better than an error
      // page), but the failure must be visible to us — a broken /datte/mf endpoint
      // otherwise looks indistinguishable from "this branch has no children".
      void this.onSelect(position, node).catch((error: unknown) => {
        console.error('[ModelFinder] selection failed — the column stays empty:', error);
      });
    });
    this.applyLabels();
  }

  /**
   * Restore the UI from a single deepest id (deep-link / localStorage). Fetches
   * the spine + every column's children, then renders the plan and selects the
   * whole path. Ends with the CTA when the deepest node is terminal. Never
   * persists and never scrolls (restore is not a live selection — D12).
   */
  async restore(selectedId: NodeId): Promise<void> {
    const known = await this.fetchSeed(selectedId);
    const path = known ? this.treeService.pathTo(selectedId) : [];
    if (path.length === 0) {
      // Unknown id → render the bare root finder, no preselect.
      await this.treeService.ensureChildren(ROOT_ID);
      this.render(null);
      return;
    }

    this.selection.set(selectedId);

    // Fetch the children of root + every path node so each column has its data
    // BEFORE the single atomic render. Deduped inside ensureChildren.
    const parents: Array<NodeId | null> = [null, ...path.map((n) => n.id)];
    await Promise.all(parents.map((p) => this.treeService.ensureChildren(p ?? ROOT_ID)));

    this.render(path, { scroll: false });
  }

  /**
   * Fetch the seed's subtree; false when the server does not know the id. Two
   * shapes mean "unknown": the `alt` source answers 200 with no nodes (pathTo is
   * then empty), the `datte` source answers 404 — for an unknown public id and for
   * every integer, which a returning visitor's localStorage still holds from before
   * the public-id switch (W0-4). Any other failure still propagates.
   */
  private async fetchSeed(selectedId: NodeId): Promise<boolean> {
    try {
      await this.treeService.ensureChildren(selectedId);
      return true;
    } catch (error: unknown) {
      if (error instanceof EndpointError && error.status === 404) return false;
      throw error;
    }
  }

  /**
   * Reveal the restored selection as a modal-open affordance (the ONE D12
   * exception — ADR-0010, 2026-06-09). Delegates to the view's
   * `revealSelectionOnOpen` ONLY when a selection is actually present (a preselect
   * was restored); with no selection there is nothing to scroll to and D12 is
   * untouched. The CALLER (`PageFinderController`) owns the policy of WHEN to call
   * this — modal context + preselect seed, after `view.layout()` — so this core
   * stays surface-agnostic and never scrolls on its own restore/render path.
   */
  revealSelectionOnOpen(): void {
    if (this.selection.selectedId === null) return;
    this.view.revealSelectionOnOpen();
  }

  /**
   * Re-finalize the view's CTA/model presentation once geometry is final (companion
   * to `revealSelectionOnOpen`, ADR-0010 2026-06-09). The restore `showCta` ran in
   * `boot()` BEFORE `view.layout()`, so a width-dependent presentation branch may
   * have been decided against a not-yet-narrow modal. The controller calls this AFTER
   * `layout()` (same modal+preselect gate) so the view re-routes the model image to
   * the correct slot. Gated on a present selection — with no preselect there is no
   * presentation to finalize. Surface-agnostic: the view decides whether it has
   * anything to re-finalize (SSR no-ops).
   */
  finalizePresentationAfterLayout(): void {
    if (this.selection.selectedId === null) return;
    this.view.finalizePresentationAfterLayout();
  }

  /** One live selection: update the single id, branch on `hasChildren`. */
  private async onSelect(position: Position, node: Node | null): Promise<void> {
    if (!node) {
      // Clear at this position: drop the deepest selection back to the parent of
      // the cleared column (single-id model). The surviving parent columns stay;
      // re-render the (now shorter) plan for the parent selection.
      const parent = this.parentOfPosition(position);
      this.selection.set(parent?.id ?? null);
      this.treeService.resetLabelsAfter(this.levelAtPosition(position) - 1);
      await this.renderForSelection(parent ? this.treeService.pathTo(parent.id) : null, {
        scroll: false,
      });
      this.persist();
      this.onAfterSelect?.(this.selection.selectedId);
      return;
    }

    this.selection.set(node.id);
    this.treeService.resetLabelsAfter(node.level); // drop stale deeper "Serie 1" overrides

    // A childless node normally needs NO fetch (AC-S10): its plan is the parent's,
    // resolved by the spine fallback (D25). That fallback cannot reach a TOP-LEVEL
    // childless node though — its spine is only itself, and the root plan is keyed
    // under ROOT_ID, which is never a spine member. `columnPlan()` would then be []
    // and `renderPlan([])` tears every column shell down: a blank finder.
    // This happens for a manufacturer whose sub-categories are not yet synced from
    // the ERP (Segway in production). Fetching adopts the SERVER's plan for the id
    // (just its own column), so the node stays selected and its CTA links to its
    // category page — matching the SSR variant's behaviour.
    // NOTE: only reachable when the plan is genuinely absent, so model leaves keep
    // their no-fetch fast path.
    if (node.hasChildren || this.treeService.columnPlan(node.id).length === 0) {
      await this.treeService.ensureChildren(node.id);
    }
    // Re-render the plan for the new deepest selection. childrenOf the terminal
    // node is empty, so its plan simply has no further column (no fetch needed).
    await this.renderForSelection(this.treeService.pathTo(node.id), { scroll: true });

    this.persist();
    this.onAfterSelect?.(this.selection.selectedId);
  }

  /**
   * Render the plan + re-select the path for the current selection.
   * `path` is the cached spine root→selected (or `null` for the idle root).
   */
  private async renderForSelection(
    path: Node[] | null,
    opts: { scroll: boolean }
  ): Promise<void> {
    await this.treeService.ensureChildren(ROOT_ID); // column 0 always exists
    this.render(path && path.length > 0 ? path : null, opts);
  }

  /**
   * The atomic plan render: reconcile the positional shells to the plan, fill
   * each column from the cached tree, select the path, set the CTA, and — only
   * on a live selection — scroll the active column into view.
   */
  private render(path: Node[] | null, opts: { scroll: boolean } = { scroll: false }): void {
    const selectedId = this.selection.selectedId;
    // The plan for the current deepest selection (D6); the root plan when idle.
    const plan = this.treeService.columnPlan(selectedId ?? ROOT_ID);

    const safePath = path ?? [];
    const itemsByPosition = this.buildItemsByPosition(plan.length, safePath);

    this.view.renderPlan(plan, itemsByPosition);

    // Select each path node at its position (D11: after renderPlan for that pos).
    // The array index is the column position — brand it at the view boundary.
    safePath.forEach((node, position) => this.view.select(positionOf(position), node.id));

    this.applyLabels();

    const deepest = safePath[safePath.length - 1];
    if (deepest && !deepest.hasChildren && deepest.url) {
      // `live` mirrors the scroll-on-live rule (D12): the vertical bottom-image
      // auto-scroll fires only on a real selection, never during deep-link restore.
      this.view.showCta({
        url: deepest.url,
        node: deepest,
        pathNames: safePath.map((n) => n.name),
        live: opts.scroll,
      });
    } else {
      this.view.showCta(null);
    }

    if (opts.scroll && safePath.length > 0) {
      // The active column is the one AFTER the deepest selection (its children),
      // or the deepest selection's own column when terminal.
      const activePosition = deepest && deepest.hasChildren
        ? Math.min(safePath.length, plan.length - 1)
        : safePath.length - 1;
      // Arithmetic result is a plain number — brand it explicitly at the boundary.
      this.view.scrollActiveIntoView(positionOf(activePosition));
    }
  }

  /**
   * Build the ordered per-position item lists: position 0 = children of root,
   * position i = children of the selected node at position i-1 (= `path[i-1]`).
   * The spine levels are the ascending prefix of the plan, so `path[i-1]` is the
   * selected node feeding column i. Positions past the path resolve to `[]`.
   */
  private buildItemsByPosition(length: number, path: Node[]): Node[][] {
    const items: Node[][] = [];
    for (let position = 0; position < length; position++) {
      // Position 0 = the root's children (top-level nodes have parentId === null,
      // which is the TreeService's "root" key — NOT the virtual ROOT_ID 0 used in
      // the URL). Position i = the children of the selected node at position i-1.
      const parentId = position === 0 ? null : (path[position - 1]?.id ?? null);
      items.push(position > 0 && parentId === null ? [] : this.treeService.childrenOf(parentId));
    }
    return items;
  }

  /** The selected node feeding `position`'s column (its parent), or null for column 0. */
  private parentOfPosition(position: Position): Node | null {
    if (position <= 0) return null;
    // No selection → no parent. An explicit null path, NOT a sentinel id: the old
    // `nodeId(-1)` "none" sentinel collided with `alt`'s real virtual series id
    // `-1` (W0-4 spec §3.4) and would have resolved that node's spine.
    const selectedId = this.selection.selectedId;
    if (selectedId === null) return null;
    return this.treeService.pathTo(selectedId)[position - 1] ?? null;
  }

  /**
   * The level number the plan currently assigns to `position` (label/clear
   * bookkeeping only). Returns a `Level` — this is the SOLE producer of the level
   * fed into `resetLabelsAfter` at the position→level mix site (ADR-0011 §3,
   * Information Gap §10.1): keeping it the only producer is what guards that site.
   */
  private levelAtPosition(position: Position): Level {
    const plan = this.treeService.columnPlan(this.selection.selectedId ?? ROOT_ID);
    // `position + 1` is a plain number; the plan slot is a `Level`. The fallback
    // is a structural 1-based level for an out-of-plan position — clamp into the
    // `Level` union (positions never exceed 6 columns; ADR-0009 plan invariant).
    return plan[position] ?? ((position + 1) as Level);
  }

  /** Persist just the deepest selected id (single-id state). */
  private persist(): void {
    ModelFinderStateManager.save(this.selection.selectedId);
  }

  /**
   * Re-apply the data-driven labels: the `/levels` rows, with each per-level
   * `title` overridden by the tree's latest `levelLabels` (the adaptive
   * "Serie"↔"Serie 1" rule). Keyed by level — the view maps level → the position
   * currently showing that level.
   */
  private applyLabels(): void {
    const overrides = this.treeService.labels();
    const map = new Map<Level, LevelLabel>();
    for (const row of this.baseLabels) {
      const override = overrides[String(row.level)];
      map.set(row.level, {
        level: row.level,
        title: override?.title ?? row.title,
        selectLabel: row.selectLabel,
        clearLabel: row.clearLabel,
      });
    }
    this.view.applyLabels(map);
  }
}
